import DashboardLayout from "@/components/DashboardLayout";
import { PermissionGuard } from "@/components/PermissionGuard";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { trpc } from "@/lib/trpc";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import { BarChart2, Send, CheckCircle, Eye, XCircle, TrendingUp, Users, Megaphone } from "lucide-react";

const COLORS = ["#25D366", "#128C7E", "#075E54", "#34B7F1", "#ef4444"];

function StatCard({ title, value, sub, icon: Icon, color }: {
  title: string; value: number | string; sub?: string; icon: any; color: string;
}) {
  return (
    <Card>
      <CardContent className="pt-5 pb-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs text-muted-foreground font-medium">{title}</p>
            <p className={`text-2xl font-bold mt-1 ${color}`}>{value}</p>
            {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
          </div>
          <div className={`p-2 rounded-lg bg-muted/50`}>
            <Icon className={`h-5 w-5 ${color}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload?.length) {
    return (
      <div className="bg-background border rounded-lg shadow-lg p-3 text-xs space-y-1">
        <p className="font-semibold text-foreground mb-1">{label}</p>
        {payload.map((p: any) => (
          <div key={p.name} className="flex items-center gap-2">
            <div className="h-2 w-2 rounded-full" style={{ backgroundColor: p.color }} />
            <span className="text-muted-foreground">{p.name}:</span>
            <span className="font-medium">{p.value}</span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

export default function WhatsAppReports() {
  const { data: stats } = trpc.dashboard.stats.useQuery();
  const { data: campaigns } = trpc.campaigns.list.useQuery();

  // Calcular métricas das campanhas
  const campaignMetrics = (campaigns ?? []).map((c: any) => ({
    name: c.name.length > 18 ? c.name.slice(0, 18) + "…" : c.name,
    Enviados: c.sentCount ?? 0,
    Entregues: c.deliveredCount ?? 0,
    Lidos: c.readCount ?? 0,
    Falhas: c.failedCount ?? 0,
  }));

  const totalSent = (campaigns ?? []).reduce((acc: number, c: any) => acc + (c.sentCount ?? 0), 0);
  const totalDelivered = (campaigns ?? []).reduce((acc: number, c: any) => acc + (c.deliveredCount ?? 0), 0);
  const totalRead = (campaigns ?? []).reduce((acc: number, c: any) => acc + (c.readCount ?? 0), 0);
  const totalFailed = (campaigns ?? []).reduce((acc: number, c: any) => acc + (c.failedCount ?? 0), 0);

  const deliveryRate = totalSent > 0 ? Math.round((totalDelivered / totalSent) * 100) : 0;
  const readRate = totalDelivered > 0 ? Math.round((totalRead / totalDelivered) * 100) : 0;

  const pieData = [
    { name: "Lidos", value: totalRead },
    { name: "Entregues (não lidos)", value: Math.max(0, totalDelivered - totalRead) },
    { name: "Enviados (não entregues)", value: Math.max(0, totalSent - totalDelivered) },
    { name: "Falhas", value: totalFailed },
  ].filter(d => d.value > 0);

  // Status das campanhas para gráfico de pizza
  const statusData = [
    { name: "Enviadas", value: (campaigns ?? []).filter((c: any) => c.status === "sent").length },
    { name: "Agendadas", value: (campaigns ?? []).filter((c: any) => c.status === "scheduled").length },
    { name: "Rascunhos", value: (campaigns ?? []).filter((c: any) => c.status === "draft").length },
    { name: "Falharam", value: (campaigns ?? []).filter((c: any) => c.status === "failed").length },
  ].filter(d => d.value > 0);

  return (
    <PermissionGuard permission="reports:view_whatsapp">
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-xl font-bold flex items-center gap-2">
            <BarChart2 className="h-5 w-5 text-primary" /> Relatórios & Analytics
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Visão geral do desempenho das suas campanhas WhatsApp
          </p>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard title="Total Enviados" value={totalSent} sub="em todas as campanhas" icon={Send} color="text-blue-600" />
          <StatCard title="Entregues" value={totalDelivered} sub={`${deliveryRate}% de taxa`} icon={CheckCircle} color="text-green-600" />
          <StatCard title="Lidos" value={totalRead} sub={`${readRate}% dos entregues`} icon={Eye} color="text-primary" />
          <StatCard title="Falhas" value={totalFailed} sub="não entregues" icon={XCircle} color="text-destructive" />
        </div>

        {/* Métricas gerais */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          <StatCard title="Total de Contatos" value={(stats as any)?.contacts ?? 0} sub="na base" icon={Users} color="text-violet-600" />
          <StatCard title="Templates" value={(stats as any)?.templates ?? 0} sub="cadastrados" icon={TrendingUp} color="text-orange-600" />
          <StatCard title="Campanhas" value={(stats as any)?.campaigns ?? 0} sub="criadas" icon={Megaphone} color="text-pink-600" />
        </div>

        {/* Gráfico de barras por campanha */}
        {campaignMetrics.length > 0 ? (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <BarChart2 className="h-4 w-4 text-primary" /> Desempenho por Campanha
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={campaignMetrics} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="Enviados" fill="#3b82f6" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="Entregues" fill="#22c55e" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="Lidos" fill="#25D366" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="Falhas" fill="#ef4444" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        ) : null}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Pizza: distribuição de mensagens */}
          {pieData.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Distribuição de Mensagens</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={85}
                      paddingAngle={3}
                      dataKey="value"
                    >
                      {pieData.map((_, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v: any) => [v, ""]} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* Pizza: status das campanhas */}
          {statusData.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Status das Campanhas</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie
                      data={statusData}
                      cx="50%"
                      cy="50%"
                      outerRadius={85}
                      paddingAngle={3}
                      dataKey="value"
                    >
                      {statusData.map((_, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v: any) => [v, ""]} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Tabela de campanhas com métricas */}
        {(campaigns ?? []).length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Detalhamento por Campanha</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/30">
                      <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Campanha</th>
                      <th className="text-center px-3 py-2.5 font-medium text-muted-foreground">Status</th>
                      <th className="text-right px-3 py-2.5 font-medium text-muted-foreground">Enviados</th>
                      <th className="text-right px-3 py-2.5 font-medium text-muted-foreground">Entregues</th>
                      <th className="text-right px-3 py-2.5 font-medium text-muted-foreground">Lidos</th>
                      <th className="text-right px-4 py-2.5 font-medium text-muted-foreground">Taxa Leitura</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(campaigns as any[]).map((c: any) => {
                      const rate = c.deliveredCount > 0
                        ? Math.round((c.readCount / c.deliveredCount) * 100)
                        : 0;
                      return (
                        <tr key={c.id} className="border-b last:border-0 hover:bg-muted/20 transition-colors">
                          <td className="px-4 py-2.5 font-medium max-w-[180px] truncate">{c.name}</td>
                          <td className="px-3 py-2.5 text-center">
                            <Badge
                              variant="secondary"
                              className={`text-xs ${
                                c.status === "sent" ? "bg-green-100 text-green-700" :
                                c.status === "scheduled" ? "bg-blue-100 text-blue-700" :
                                c.status === "failed" ? "bg-red-100 text-red-700" :
                                "bg-muted text-muted-foreground"
                              }`}
                            >
                              {c.status === "sent" ? "Enviada" :
                               c.status === "scheduled" ? "Agendada" :
                               c.status === "draft" ? "Rascunho" :
                               c.status === "sending" ? "Enviando" : "Falhou"}
                            </Badge>
                          </td>
                          <td className="px-3 py-2.5 text-right text-blue-600 font-medium">{c.sentCount ?? 0}</td>
                          <td className="px-3 py-2.5 text-right text-green-600 font-medium">{c.deliveredCount ?? 0}</td>
                          <td className="px-3 py-2.5 text-right text-primary font-medium">{c.readCount ?? 0}</td>
                          <td className="px-4 py-2.5 text-right">
                            <span className={`font-semibold ${rate >= 50 ? "text-primary" : rate >= 25 ? "text-orange-500" : "text-muted-foreground"}`}>
                              {rate}%
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Estado vazio */}
        {(campaigns ?? []).length === 0 && (
          <Card>
            <CardContent className="py-16 text-center">
              <BarChart2 className="h-10 w-10 mx-auto text-muted-foreground/30 mb-3" />
              <p className="font-medium text-muted-foreground">Nenhum dado disponível ainda</p>
              <p className="text-sm text-muted-foreground mt-1">
                Crie e envie campanhas para ver os relatórios de desempenho aqui.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
    </PermissionGuard>
  );
}
